import { moment } from '@eventbrite/date';
import { CreditCardBack, Pencil } from '@eventbrite/eds-iconography';
import { getEventInfo } from '../../api/event';
import { FinancialInformationFromatted } from '../../api/types';
import {
    ADD_BANK_ACCOUNT,
    ADD_FINANCIAL_INFO,
    COMPLETE_TAX_PAYER_INFORMATION,
    DOCUMENT_REQUEST_STATUS_FULFILLED,
    MAKE_SURE_YOU_GET_PAID,
    SET_UP_YOUR_ACCOUNT,
} from '../../constants';
import { NotificationMessageType } from '../../types';
import {
    createNotificationsLink,
    getNotificationMessage,
    hasFinancialInfoUncompleted,
} from '../../utils';
import { ModalFinancialMissingInformation } from '../types';

export const getFinancialMissingInformationModalNotificationMessage = ({
    currency,
    endDate,
    eventName,
    locale,
    payoutAmount,
}: NotificationMessageType) => {
    if (payoutAmount > 0) {
        const { title, description } = getNotificationMessage({
            currency,
            endDate,
            eventName,
            locale,
            payoutAmount,
        });
        return { title, description };
    }

    const title = MAKE_SURE_YOU_GET_PAID;
    const description = SET_UP_YOUR_ACCOUNT;

    return { title, description };
};

export const getFinancialMissingInfoData = async (
    data: FinancialInformationFromatted | {},
    storedStatus: string | null,
): Promise<ModalFinancialMissingInformation | {}> => {
    const {
        isPayoutInfoMissing,
        isTaxpayerInfoMissing,
        eventInformation,
        isEventStartingThisWeek,
        hasTicketsSold,
        organizationInformation,
    } = data as FinancialInformationFromatted;
    const {
        country,
        currency,
        eventId,
        endDate,
        eventName,
        locale,
        payoutAmount,
    } = eventInformation || {};

    const { hasPayoutMethods } = organizationInformation || {};

    const isEligibleForMissingFinancialInfoNotification =
        isPayoutInfoMissing && isTaxpayerInfoMissing;

    const hasCompletedTaxpayerInformationLocally =
        storedStatus === DOCUMENT_REQUEST_STATUS_FULFILLED;

    const hasFinancialInfoMissing = hasFinancialInfoUncompleted(
        isEligibleForMissingFinancialInfoNotification,
        hasCompletedTaxpayerInformationLocally,
    );

    const eventPayoutInfoMissing = eventId && (await getEventInfo(eventId));

    const isEventEnded =
        eventPayoutInfoMissing &&
        moment().isAfter(moment(eventPayoutInfoMissing.endDate));

    const isModalNotificationVisible = isEventStartingThisWeek || isEventEnded;

    const eventDetails = {
        hasTicketsSold,
        hasFinancialInfoMissing,
        isEventEnded,
        ...eventPayoutInfoMissing,
    };
    const { title, description } =
        getFinancialMissingInformationModalNotificationMessage({
            endDate,
            locale,
            eventName,
            currency,
            payoutAmount,
        });

    const financialMissingInformationData = {
        //TODO: Statsig po_request_1st_payout_method_on_finance_workspace. Remove isEligibleForFirstPayoutMethodExperiment after experiment finish
        isEligibleForFirstPayoutMethodExperiment: hasPayoutMethods === false,
        modalNotification: isModalNotificationVisible,
        message: {
            title,
            subTitle: description,
            buttonText: ADD_FINANCIAL_INFO,
            buttonLink: createNotificationsLink({
                country,
                currency,
                eventId,
                hasFinancialInfoMissing,
            }),
        },
        actions: [{}],
    };

    if (hasFinancialInfoMissing) {
        return {
            ...financialMissingInformationData,
            event: eventDetails,
            actions: [
                {
                    icon: CreditCardBack,
                    text: ADD_BANK_ACCOUNT,
                },
                {
                    icon: Pencil,
                    text: COMPLETE_TAX_PAYER_INFORMATION,
                },
            ],
        };
    } else if (isPayoutInfoMissing) {
        return {
            ...financialMissingInformationData,
            event: eventDetails,
            country,
            actions: [
                {
                    icon: CreditCardBack,
                    text: ADD_BANK_ACCOUNT,
                },
            ],
        };
    }

    return {};
};
